import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import MergeIcon from '@persona-id/design-tokens/icons/MergeIcon';
import { DateTime } from 'luxon';
import { FC } from 'react';
import styled from 'styled-components';

import TransactionStatusSticker from 'dashboard/components/TransactionStatusSticker';
import { RedactedObjectPath } from 'dashboard/models/event';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionLabel } from 'dashboard/models/transaction-label';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import Attribute, { AttributeGrid, AttributeLabel } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

const HeroIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: ${Color.CorePurple100};
  border-radius: ${BorderRadius.Lg};
  margin-right: ${Spacing[2]};
`;

const DualPane = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing[5]};
`;

const StyledAttributeGrid = styled(AttributeGrid)`
  border-collapse: separate;
  border-spacing: 0 ${Spacing[1]};

  & + & {
    padding-bottom: 0;
  }

  & > div + div {
    dt,
    dd {
      border-top: 1px dashed ${Color.BorderDivider};
      padding-top: ${Spacing[1]};
    }
  }
`;

const NonCapitalAttributeLabel = styled(AttributeLabel).attrs({
  size: 'sm',
})``;

interface TransactionSummaryCardProps {
  redactedObjectPaths: RedactedObjectPath[];
  transaction: Transaction;
  transactionLabel: TransactionLabel | null;
  transactionType: TransactionType;
  transactionStatuses: TransactionStatus[];
}

const TransactionSummaryCard: FC<TransactionSummaryCardProps> = ({
  redactedObjectPaths,
  transaction,
  transactionLabel,
  transactionType,
  transactionStatuses,
}) => {
  const preferExternalIdEnabled = useFeatureFlag('dashboard:txn-prefer-external-id');

  const transactionStatus = transactionStatuses.find((transactionStatus) => {
    return transactionStatus.id === transaction.relationships['transaction-status'].data?.id;
  });
  const redactedPaths = redactedObjectPaths.reduce<string[]>((paths, redactedObjectPath) => {
    return [...paths, redactedObjectPath.path];
  }, []);

  return (
    <Card>
      <Card.Body>
        <Card.Title>
          <HeroIcon>
            <MergeIcon color={Color.BorderIconActive} size='24' />
          </HeroIcon>
          {transactionType.attributes.name}
        </Card.Title>
        <DualPane>
          <StyledAttributeGrid horizontal>
            <Attribute
              label='Status'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              labelComponent={NonCapitalAttributeLabel}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              value={<TransactionStatusSticker transactionStatus={transactionStatus} />}
            />
            <Attribute
              label='Created at'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              labelComponent={NonCapitalAttributeLabel}
              value={DateTime.fromISO(transaction.attributes['created-at']).toFormat(
                DATE_AND_TIME_TZ_FORMAT,
              )}
            />
            {transactionLabel && (
              <Attribute
                label='Model Label'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                labelComponent={NonCapitalAttributeLabel}
                value={transactionLabel.attributes['model-label']}
              />
            )}
          </StyledAttributeGrid>
          <StyledAttributeGrid horizontal>
            <Attribute
              label='Transaction ID'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              labelComponent={NonCapitalAttributeLabel}
              value={transaction.id}
            />
            {preferExternalIdEnabled ? (
              <Attribute
                label='External ID'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                labelComponent={NonCapitalAttributeLabel}
                placeholder='No external ID'
                value={
                  !transaction.attributes['redacted-at'] && !redactedPaths.includes('external-id')
                    ? transaction.attributes['external-id']
                    : 'dummy value' // Attribute component will only display 'Redacted' sticker when a value is present
                }
                redacted={
                  transaction.attributes['redacted-at'] != null ||
                  redactedPaths.includes('external-id')
                }
              />
            ) : (
              <Attribute
                label='Reference ID'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                labelComponent={NonCapitalAttributeLabel}
                placeholder='No reference ID'
                value={
                  !transaction.attributes['redacted-at'] && !redactedPaths.includes('reference-id')
                    ? transaction.attributes['reference-id']
                    : 'dummy value' // Attribute component will only display 'Redacted' sticker when a value is present
                }
                redacted={
                  transaction.attributes['redacted-at'] != null ||
                  redactedPaths.includes('reference-id')
                }
              />
            )}
            {transactionLabel && (
              <Attribute
                label='Model Version'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                labelComponent={NonCapitalAttributeLabel}
                value={transactionLabel.attributes['model-version']}
              />
            )}
          </StyledAttributeGrid>
        </DualPane>
      </Card.Body>
    </Card>
  );
};

export default TransactionSummaryCard;
